import { useEffect, useState, useCallback } from 'react'
import { listAiLogs, getAiLog } from '@/api/resources'
import {
  PageHeader, SearchInput, Badge, Spinner, EmptyState, TableFooter,
} from '@/components/ui/primitives'
import { Bot, X, CheckCircle2, XCircle, Clock, ChevronRight } from 'lucide-react'

const PAGE_SIZE = 20

const PROVIDER_COLOR = { gemini: 'blue', openai: 'green', claude: 'amber' }

function fmt(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('uz-UZ', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  })
}

// ── Detail modal ──────────────────────────────────────────────────────
function DetailModal({ logId, onClose }) {
  const [data,    setData]    = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    getAiLog(logId)
      .then(r => setData(r.data))
      .finally(() => setLoading(false))
  }, [logId])

  return (
    <>
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="w-full max-w-2xl rounded-2xl border flex flex-col shadow-xl overflow-hidden bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-700 max-h-[90vh]">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-slate-700 flex-shrink-0">
            <span className="font-bold text-sm text-gray-800 dark:text-white flex items-center gap-2">
              <Bot size={16} /> Log #{logId}
            </span>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 text-gray-400">
              <X size={16} />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
            {loading && <div className="flex justify-center py-10"><Spinner /></div>}
            {data && (
              <>
                {/* Meta */}
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {[
                    ['Provider',    <Badge variant={PROVIDER_COLOR[data.provider] ?? 'gray'}>{data.provider}</Badge>],
                    ['Model',       <code className="text-xs bg-gray-100 dark:bg-slate-800 px-1.5 py-0.5 rounded">{data.model}</code>],
                    ['Holat',       data.success
                      ? <span className="flex items-center gap-1 text-green-600"><CheckCircle2 size={14}/> Muvaffaqiyatli</span>
                      : <span className="flex items-center gap-1 text-red-500"><XCircle size={14}/> Xato</span>],
                    ['Vaqt',        <span className="flex items-center gap-1 text-gray-500"><Clock size={13}/>{data.duration_ms} ms</span>],
                    ['Ball',        data.score_given != null ? `${data.score_given} / ${data.max_score}` : '—'],
                    ['Talaba',      data.student ?? '—'],
                    ['Imtihon',     data.exam ?? '—'],
                    ['Sana',        fmt(data.created_at)],
                  ].map(([label, val]) => (
                    <div key={label}>
                      <p className="text-xs font-medium text-gray-400 dark:text-slate-500 uppercase tracking-wide mb-0.5">{label}</p>
                      <div className="text-gray-800 dark:text-slate-200">{val}</div>
                    </div>
                  ))}
                </div>

                {/* Error */}
                {data.error && (
                  <div className="rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-4 py-3">
                    <p className="text-xs font-semibold text-red-600 dark:text-red-400 mb-1 uppercase tracking-wide">Xato</p>
                    <pre className="text-xs text-red-700 dark:text-red-300 whitespace-pre-wrap">{data.error}</pre>
                  </div>
                )}

                {/* Prompt */}
                <div>
                  <p className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wide mb-1">Prompt</p>
                  <pre className="text-xs bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-3 whitespace-pre-wrap text-gray-700 dark:text-slate-300 max-h-48 overflow-y-auto">
                    {data.prompt}
                  </pre>
                </div>

                {/* Response */}
                {data.response && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wide mb-1">AI javobi</p>
                    <pre className="text-xs bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-3 whitespace-pre-wrap text-gray-700 dark:text-slate-300 max-h-48 overflow-y-auto">
                      {data.response}
                    </pre>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

// ── Main page ─────────────────────────────────────────────────────────
export default function AiLogsPage() {
  const [rows,      setRows]      = useState([])
  const [loading,   setLoading]   = useState(true)
  const [search,    setSearch]    = useState('')
  const [provider,  setProvider]  = useState('')
  const [success,   setSuccess]   = useState('')
  const [page,      setPage]      = useState(1)
  const [total,     setTotal]     = useState(0)
  const [detailId,  setDetailId]  = useState(null)

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  const load = useCallback(() => {
    setLoading(true)
    const params = { page, page_size: PAGE_SIZE }
    if (search)   params.search   = search
    if (provider) params.provider = provider
    if (success)  params.success  = success
    listAiLogs(params)
      .then(r => {
        const d = r.data
        setRows(Array.isArray(d) ? d : (d.results ?? []))
        setTotal(Array.isArray(d) ? d.length : (d.count ?? 0))
      })
      .finally(() => setLoading(false))
  }, [page, search, provider, success])

  useEffect(() => { load() }, [load])

  // Reset to page 1 when filters change
  useEffect(() => { setPage(1) }, [search, provider, success])

  return (
    <div className="space-y-5">
      <PageHeader title="AI So'rovlar logi" subtitle={`Jami: ${total} ta yozuv`} />

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <SearchInput
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Talaba nomi, xato matni..."
          className="flex-1 min-w-48"
        />
        <select
          value={provider}
          onChange={e => setProvider(e.target.value)}
          className="text-sm border border-gray-200 dark:border-slate-700 rounded-xl px-3 py-2 bg-white dark:bg-slate-900 text-gray-700 dark:text-slate-200 focus:outline-none focus:border-blue-500"
        >
          <option value="">Barcha provayderlar</option>
          <option value="gemini">Gemini</option>
          <option value="openai">OpenAI</option>
          <option value="claude">Claude</option>
        </select>
        <select
          value={success}
          onChange={e => setSuccess(e.target.value)}
          className="text-sm border border-gray-200 dark:border-slate-700 rounded-xl px-3 py-2 bg-white dark:bg-slate-900 text-gray-700 dark:text-slate-200 focus:outline-none focus:border-blue-500"
        >
          <option value="">Barcha holatlar</option>
          <option value="true">Muvaffaqiyatli</option>
          <option value="false">Xatolik</option>
        </select>
      </div>

      {/* Table */}
      <div className="rounded-2xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16"><Spinner size="lg" /></div>
        ) : rows.length === 0 ? (
          <EmptyState icon={<Bot size={32} />} title="Loglar yo'q" description="Hali birorta AI so'rov amalga oshirilmagan." />
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 dark:border-slate-800 bg-gray-50 dark:bg-slate-800/50">
                {['Sana', 'Talaba', 'Imtihon', 'Provayder', 'Ball', 'Vaqt (ms)', 'Holat', ''].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wide whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50 dark:divide-slate-800">
              {rows.map(row => (
                <tr
                  key={row.id}
                  onClick={() => setDetailId(row.id)}
                  className="hover:bg-gray-50 dark:hover:bg-slate-800/50 cursor-pointer transition-colors"
                >
                  <td className="px-4 py-3 text-gray-500 dark:text-slate-400 whitespace-nowrap text-xs">{fmt(row.created_at)}</td>
                  <td className="px-4 py-3 font-medium text-gray-800 dark:text-slate-200 max-w-[140px] truncate">{row.student ?? '—'}</td>
                  <td className="px-4 py-3 text-gray-600 dark:text-slate-300 max-w-[160px] truncate">{row.exam ?? '—'}</td>
                  <td className="px-4 py-3">
                    <Badge variant={PROVIDER_COLOR[row.provider] ?? 'gray'}>{row.provider}</Badge>
                  </td>
                  <td className="px-4 py-3 font-mono text-gray-700 dark:text-slate-300 whitespace-nowrap">
                    {row.score_given != null ? `${row.score_given} / ${row.max_score}` : '—'}
                  </td>
                  <td className="px-4 py-3 text-gray-500 dark:text-slate-400 whitespace-nowrap">
                    {row.duration_ms != null ? `${row.duration_ms} ms` : '—'}
                  </td>
                  <td className="px-4 py-3">
                    {row.success
                      ? <span className="flex items-center gap-1 text-green-600 text-xs font-medium"><CheckCircle2 size={13}/> OK</span>
                      : <span className="flex items-center gap-1 text-red-500 text-xs font-medium"><XCircle size={13}/> Xato</span>
                    }
                  </td>
                  <td className="px-4 py-3 text-gray-300 dark:text-slate-600">
                    <ChevronRight size={14} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {!loading && total > PAGE_SIZE && (
        <TableFooter
          page={page}
          totalPages={totalPages}
          pageSize={PAGE_SIZE}
          totalCount={total}
          onPageChange={setPage}
        />
      )}

      {/* Detail modal */}
      {detailId && <DetailModal logId={detailId} onClose={() => setDetailId(null)} />}
    </div>
  )
}
